import { FC, MouseEvent } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { useStanRouter } from '@/hooks/useStanRouter';

interface StanLinkProps extends Omit<LinkProps, 'to'> {
    to: string;
    skipDirtyCheck?: boolean;
}

/**
 * StanLink - A wrapper around React Router's Link that integrates with Stan's navigation system
 *
 * This component provides:
 * - Proper accessibility with semantic HTML anchor tags
 * - Integration with Stan's dirty state checking
 * - Support for legacy route handling
 * - Navigation confirmation when there are unsaved changes
 *
 * @param to - The destination path
 * @param skipDirtyCheck - If true, bypasses dirty state checking (default: false)
 * @param onClick - Optional click handler
 * @param children - Link content
 * @param props - Additional props passed to the underlying Link component
 *
 * @example
 * ```tsx
 * // Basic usage
 * <StanLink to="/dashboard">Go to Dashboard</StanLink>
 *
 * // Skip dirty check (e.g., for cancel buttons)
 * <StanLink to="/settings" skipDirtyCheck>Cancel</StanLink>
 *
 * // With custom click handler
 * <StanLink to="/profile" onClick={(e) => console.log('Navigating...')}>
 *   Profile
 * </StanLink>
 *
 * // For button-styled links, apply button classes directly to StanLink
 * // This keeps the semantic <a> tag while looking like a button
 * <StanLink
 *   to="/settings"
 *   className="inline-flex items-center justify-center rounded-full bg-brand-main text-on-brand px-6 py-3 hover:bg-brand-hover"
 * >
 *   Settings
 * </StanLink>
 */

export const StanLink: FC<StanLinkProps> = ({ to, skipDirtyCheck = false, onClick, children, ...props }) => {
    const { push } = useStanRouter();

    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
        // Prevent default Link behavior to use Stan router for consistent animations
        e.preventDefault();

        // Call the original onClick if provided
        onClick?.(e);

        // Use Stan router for navigation (handles animations, dirty checks, etc.)
        push(to, { skipDirtyCheck });
    };

    return (
        <Link to={to} onClick={handleClick} {...props}>
            {children}
        </Link>
    );
};
